/**
 * Firebase Cloud Messaging (FCM) Integration
 *
 * Handles Firebase app initialization, FCM token management,
 * and foreground message handling with Vuex store integration.
 *
 * Architecture:
 * - Background messages: Handled by service worker (public/firebase-messaging-sw.js)
 * - Foreground messages: Handled by onMessage listener in this file
 * - Notifications: Dispatched to notifications/processFirebaseMessage action
 */

console.log('[Firebase] 🔥 Loading firebase.ts module...');

import { initializeApp, type FirebaseApp } from "firebase/app";
import { getMessaging, getToken, onMessage, type Messaging, type MessagePayload } from "firebase/messaging";
import type { Store } from 'vuex';
import type { RootState } from '@/store/types';
import firebaseConfig from "./firebaseConfig";
import { AuthService } from "@/services/AuthService";

console.log('[Firebase] 📦 Imports loaded successfully');

/**
 * Store reference - injected from main.ts
 * Used for dispatching notification actions when FCM messages arrive
 */
let store: Store<RootState> | null = null;

/**
 * Firebase app instance
 * Initialized once on module load
 */
console.log('[Firebase] 🚀 Initializing Firebase app...');
const app: FirebaseApp = initializeApp(firebaseConfig);
console.log('[Firebase] ✅ Firebase app initialized');

/**
 * Firebase Cloud Messaging instance
 * Used for FCM token management and message handling
 */
console.log('[Firebase] 📡 Getting messaging instance...');
const messaging: Messaging = getMessaging(app);
console.log('[Firebase] ✅ Messaging instance created');

/**
 * VAPID (Voluntary Application Server Identification) key
 * Required for FCM web push notifications
 */
const VAPID_KEY = "BGO601FCQBWVNarfbE4U9srR-Io-8b5VVcebafwqvkSfad5Yf8qsS1BJUWbMs7_MG2oLj1Rj7UCdJdQ1EYDtzO0";

/**
 * Initialize Firebase Cloud Messaging with Vuex store integration
 *
 * Sets up the foreground message listener that processes incoming FCM messages
 * and dispatches them to the notifications store for toast display.
 *
 * @param vuexStore - The Vuex store instance (must have notifications module)
 * @returns The Firebase Messaging instance
 *
 * @example
 * // In main.ts
 * import { initializeFirebaseMessaging } from '@/components/firebase/firebase';
 * initializeFirebaseMessaging(store);
 */
export function initializeFirebaseMessaging(vuexStore: Store<RootState>): Messaging {
  console.log('[Firebase] Initializing messaging with store:', vuexStore ? 'Store available' : 'Store is NULL');
  store = vuexStore;

  // Verify notifications module is registered
  if (store && store.state.notifications) {
    console.log('[Firebase] ✅ Notifications module found in store');
  } else {
    console.error('[Firebase] ❌ Notifications module NOT found in store!');
  }

  // Handle foreground messages
  console.log('[Firebase] Setting up onMessage listener...');
  onMessage(messaging, (payload: MessagePayload) => {
    console.log('[Firebase] ========================================');
    console.log('[Firebase] 🔔 MESSAGE ARRIVED!');
    console.log('[Firebase] Full payload:', payload);
    console.log('[Firebase] Payload.data:', payload.data);
    console.log('[Firebase] ========================================');

    // Dispatch to notifications store to show toast
    if (store) {
      console.log('[Firebase] Store is available, dispatching...');
      try {
        const result = store.dispatch('notifications/processFirebaseMessage', payload);
        console.log('[Firebase] ✅ Dispatch result:', result);
      } catch (error) {
        console.error('[Firebase] ❌ Dispatch error:', error);
        if (error instanceof Error) {
          console.error('[Firebase] Error stack:', error.stack);
        }
      }
    } else {
      console.warn('[Firebase] ⚠️ Store not available, cannot show notification');
    }
    console.log('[Firebase] ========================================');
  });
  console.log('[Firebase] ✅ onMessage listener registered');

  console.log('[Firebase] ✅ Firebase messaging initialized successfully');
  return messaging;
}

/**
 * Save FCM token to user's database entity
 *
 * Persists the FCM token to the backend so the server can send push notifications
 * to this device. Uses AuthService for API communication.
 *
 * Note: authToken is auto-injected by ApiClient, no need to pass it
 *
 * @param userUid - The user's unique identifier
 * @param fcmToken - The FCM registration token for this device
 * @throws Error if the API call fails
 *
 * @example
 * await setFcmTokenToTheUserDbEntity('user123', 'fcm_token_xyz');
 */
async function setFcmTokenToTheUserDbEntity(userUid: string, fcmToken: string): Promise<void> {
  try {
    await AuthService.saveFcmToken(userUid, fcmToken);
    console.log('[Firebase] ✅ FCM token saved to database');
  } catch (error) {
    console.error('[Firebase] ❌ Failed to save FCM token:', error);
    throw error;
  }
}

/**
 * Request FCM token for push notifications
 *
 * Requests notification permission from the browser and retrieves the FCM token.
 * This token is used by the backend to send push notifications to this device.
 *
 * @returns Promise resolving to the FCM token, or null if permission denied/error
 * @throws Error if Firebase messaging is not initialized
 *
 * @example
 * const token = await fbGetFcmToken();
 * if (token) {
 *   await setFcmTokenToTheUserDbEntity(userId, token);
 * }
 */
async function fbGetFcmToken(): Promise<string | null> {
  try {
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    console.log('[Firebase] ✅ FCM token retrieved:', token);
    return token;
  } catch (error) {
    console.error('[Firebase] ❌ Failed to get FCM token:', error);
    if (error instanceof Error) {
      console.error('[Firebase] Error message:', error.message);
    }
    return null;
  }
}

/**
 * Delete FCM token and unregister from notifications
 * 
 * Should be called on logout to ensure the device stops receiving notifications
 * from the previous account.
 * 
 * @returns Promise resolving to true if token was deleted, false otherwise
 * 
 * @example
 * await deleteFcmToken();
 */
async function deleteFcmToken(): Promise<boolean> {
  try {
    const { deleteToken } = await import('firebase/messaging');
    const deleted = await deleteToken(messaging);
    
    if (deleted) {
      console.log('[Firebase] ✅ FCM token deleted successfully');
      return true;
    } else {
      console.warn('[Firebase] ⚠️ FCM token deletion returned false');
      return false;
    }
  } catch (error) {
    console.error('[Firebase] ❌ Failed to delete FCM token:', error);
    return false;
  }
}

/**
 * Export Firebase instances and utility functions
 */
export {
  app,
  messaging,
  fbGetFcmToken,
  setFcmTokenToTheUserDbEntity,
  deleteFcmToken,
};
